import * as React from 'react';
import { Action } from 'redux';

import { ButtonType, ButtonWidget, InteractionState, TabStyles, Tooltip, TooltipPosition } from '@tableau/widgets';

import { ServiceLocator } from '../../app/ServiceLocator';
import { Messages } from '../../codegen/Localize';
import { TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { imgPath } from '../../common/utilities/ImgUrl';
import { TelemetryService } from '../../ts/services/TelemetryService';
import { HomeStyles } from '../styles/HomeStyles';
import { WelcomeScreenStyles } from '../styles/WelcomeScreenStyles';
import { WelcomeScreenActions, WelcomeScreenLinkDetails } from '../utils/WelcomeScreenLinks';
import { WelcomeScreenLinkList } from './WelcomeScreenLink';

export interface WelcomeScreenStateProps {
  readonly header: string;
  readonly subHeader: string;
  readonly bottomLinks: WelcomeScreenLinkDetails[];
  readonly primaryButtonLink: WelcomeScreenLinkDetails;
}

export interface WelcomeScreenDispatchProps {
  readonly closeWelcomeScreen: () => any;
  readonly dispatchAction: (action: Action) => any;
}

export type WelcomeScreenProps = WelcomeScreenStateProps & WelcomeScreenDispatchProps;

export class WelcomeScreen extends React.PureComponent<WelcomeScreenProps> {

  private telemetryService: TelemetryService;

  constructor(props: WelcomeScreenProps) {
    super(props);
    this.telemetryService = ServiceLocator.get<TelemetryService>('TelemetryService');
  }

  private getTooltipContent = (): Promise<JSX.Element> => {
    return new Promise(resolve => {
      // Adds a delay to the Tooltip showing. Example taken from the shared-widgets docs
      window.setTimeout(() => { resolve(<span>{Messages.home_welcomeScreen_dismiss_tooltip()}</span>); }, 350);
    });
  }

  private getCloseButtonContent = (state: InteractionState): JSX.Element => {
    const focus: boolean = state.focus || state.active;
    let closeButtonStyle = WelcomeScreenStyles.closeButton;
    return (
      <div style={focus ? {...closeButtonStyle, background: TabStyles.Colors.WhiteTransparent} : closeButtonStyle}>
        <Tooltip
          position={TooltipPosition.Right}
          testId={WelcomeScreenTestIds.closeButtonTooltip}
          getContent={this.getTooltipContent}
        >
          <img
            src={imgPath('dialogs_close_white_active.svg')}
            draggable={false}
            alt={Messages.home_welcomeScreen_dismiss_alttext()}
            style={{width: '100%', height: '100%'}}
          />
        </Tooltip>
      </div>
    );
  }

  private handleCloseButtonClick() {
    this.sendTelemetryEvent(WelcomeScreenActionTypes.dismiss, WelcomeScreenActions.dismiss);
    this.props.closeWelcomeScreen();
  }

  private sendTelemetryEvent(actionType: WelcomeScreenActionTypes, action: WelcomeScreenActions) {
    this.telemetryService.sendEvent({
      type: TelemetryEvents.HomeWelcomeChannel,
      actionType,
      action,
    });
  }

  render() {
    const primaryButtonAction = () => {
      this.sendTelemetryEvent(WelcomeScreenActionTypes.primary, this.props.primaryButtonLink.actionName);

      if (this.props.primaryButtonLink.getAction)
        return this.props.dispatchAction(this.props.primaryButtonLink.getAction());
      else {
        location.href = this.props.primaryButtonLink.url;
      }
    };

    const primaryButton =
      <ButtonWidget
        buttonType={ButtonType.LightOutline}
        handleClick={primaryButtonAction}
        testId={WelcomeScreenTestIds.callToActionButton}
      >
        {this.props.primaryButtonLink.linkTitle}
      </ButtonWidget>;

    const closeButton =
      <ButtonWidget
        buttonType={ButtonType.IconOutline}
        handleClick={() => this.handleCloseButtonClick()}
        testId={WelcomeScreenTestIds.closeButton}
        getContent={this.getCloseButtonContent}
      />;

    return (
      <section className={`${WelcomeScreenStyles.root} ${HomeStyles.contentSection}`} data-tb-test-id={WelcomeScreenTestIds.welcomeScreen}>
        <div className={WelcomeScreenStyles.closeButtonContainer}>
          {closeButton}
        </div>
        <h2 className={WelcomeScreenStyles.header}>{this.props.header}</h2>
        <p className={WelcomeScreenStyles.subheader}>{this.props.subHeader}</p>
        {primaryButton}
        <WelcomeScreenLinkList
          links={this.props.bottomLinks}
          dispatchAction={this.props.dispatchAction}
          sendTelemetryEvent={this.sendTelemetryEvent.bind(this)}
        />
      </section>
    );
  }
}

export enum WelcomeScreenActionTypes {
  primary = 'primary',
  secondary = 'secondary',
  dismiss = 'dismiss',
}

export namespace WelcomeScreenTestIds {
  export const welcomeScreen = 'welcome-screen';
  export const callToActionButton = 'welcome-screen-cta-button';
  export const closeButton = 'welcome-screen-close-button';
  export const closeButtonTooltip = 'welcome-screen-close-tooltip';
  export const welcomeScreenLink = 'welcome-screen-link';
}
